import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  consumeTourReplay,
  findFirstParentChildHref,
  findFirstTaskWorkHref,
  getAutoStartChapter,
  getChapter,
  getTourRole,
  hasCookieConsent,
  isTourHomePath,
  pathMatchesStep,
  readChapterDone,
  writeChapterDone,
} from '../tours/siteTours';

const PAD = 8;
const CARD_GAP = 12;
const NARROW = 640;
const WAIT_ATTEMPTS = 24;
const WAIT_MS = 150;

function findStepTarget(selector) {
  if (!selector) return null;
  return document.querySelector(selector);
}

function useViewport() {
  const [size, setSize] = useState(() => ({
    w: typeof window !== 'undefined' ? window.innerWidth : 1024,
    h: typeof window !== 'undefined' ? window.innerHeight : 768,
  }));

  useEffect(() => {
    function onResize() {
      setSize({ w: window.innerWidth, h: window.innerHeight });
    }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return size;
}

function computeCardStyle(rect, viewport) {
  const narrow = viewport.w < NARROW;
  const cardMaxW = Math.min(360, viewport.w - 24);

  if (narrow || !rect) {
    return {
      left: 12,
      right: 12,
      bottom: 12,
      top: 'auto',
      width: 'auto',
      maxWidth: 'none',
    };
  }

  const spaceBelow = viewport.h - rect.bottom - CARD_GAP;
  const spaceAbove = rect.top - CARD_GAP;
  const preferBelow = spaceBelow >= 160 || spaceBelow >= spaceAbove;

  let top;
  if (preferBelow) {
    top = rect.bottom + CARD_GAP;
  } else {
    top = Math.max(12, rect.top - CARD_GAP - 200);
  }

  let left = rect.left;
  if (left + cardMaxW > viewport.w - 12) {
    left = Math.max(12, viewport.w - 12 - cardMaxW);
  }
  if (left < 12) left = 12;

  return {
    left,
    top: Math.min(top, viewport.h - 220),
    width: cardMaxW,
    maxWidth: cardMaxW,
    bottom: 'auto',
    right: 'auto',
  };
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/** Затемнение с дыркой. evenodd, чтобы клики попадали в подсвеченный элемент, а не в слой тура. */
function tourShadePath(width, height, hole) {
  const outer = `M0 0H${width}V${height}H0Z`;
  if (!hole || hole.w < 1 || hole.h < 1) return outer;
  const r = Math.min(10, hole.w / 2, hole.h / 2);
  const x = hole.x;
  const y = hole.y;
  const right = x + hole.w;
  const bottom = y + hole.h;
  const inner = [
    `M${x + r} ${y}`,
    `H${right - r}`,
    `A${r} ${r} 0 0 1 ${right} ${y + r}`,
    `V${bottom - r}`,
    `A${r} ${r} 0 0 1 ${right - r} ${bottom}`,
    `H${x + r}`,
    `A${r} ${r} 0 0 1 ${x} ${bottom - r}`,
    `V${y + r}`,
    `A${r} ${r} 0 0 1 ${x + r} ${y}Z`,
  ].join('');
  return `${outer}${inner}`;
}

export default function SiteTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const viewport = useViewport();
  const role = useMemo(() => getTourRole(user), [user]);

  const [active, setActive] = useState(false);
  const [chapterId, setChapterId] = useState(null);
  const [stepIndex, setStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [targetRect, setTargetRect] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [cookieOk, setCookieOk] = useState(() => hasCookieConsent());

  const runIdRef = useRef(0);
  const locationRef = useRef(location);

  useEffect(() => {
    locationRef.current = location;
  }, [location]);

  const finish = useCallback(() => {
    runIdRef.current += 1;
    const doneRole = role;
    const doneChapter = chapterId;
    if (doneRole && doneChapter) writeChapterDone(doneRole, doneChapter);
    setActive(false);
    setPreparing(false);
    setChapterId(null);
    setStepIndex(0);
    setSteps([]);
    setTargetRect(null);
    if (doneRole && doneChapter) {
      window.dispatchEvent(
        new CustomEvent('diobuch-tour-finished', {
          detail: { role: doneRole, chapterId: doneChapter },
        }),
      );
    }
  }, [role, chapterId]);

  const prepareStep = useCallback(
    async (stepList, index, runId) => {
      const step = stepList[index];
      if (!step) return { action: 'finish' };

      setPreparing(true);

      const pathReached = (targetPath, pathname) => {
        if (!targetPath) return true;
        if (targetPath === '/') return pathname === '/' || pathname === '/dashboard';
        return pathname === targetPath || pathname.startsWith(`${targetPath}/`);
      };

      const ensurePath = async (targetPath) => {
        if (!targetPath) return;
        if (pathReached(targetPath, locationRef.current.pathname)) return;
        navigate(targetPath);
        for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
          if (runId !== runIdRef.current) return;
          await sleep(WAIT_MS);
          if (pathReached(targetPath, locationRef.current.pathname)) return;
        }
      };

      // Resolve dynamic links (первая задача / ребёнок / вкладка).
      if (step.resolve === 'first-task-work') {
        if (!pathMatchesStep(step, locationRef.current.pathname)) {
          await ensurePath(step.resolveFrom || '/tasks');
          let href = null;
          for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
            if (runId !== runIdRef.current) return { action: 'abort' };
            href = findFirstTaskWorkHref();
            if (href) break;
            await sleep(WAIT_MS);
          }
          if (!href) {
            setPreparing(false);
            return step.optional ? { action: 'skip' } : { action: 'show' };
          }
          navigate(href);
          for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
            if (runId !== runIdRef.current) return { action: 'abort' };
            await sleep(WAIT_MS);
            if (pathMatchesStep(step, locationRef.current.pathname)) break;
          }
        }
      } else if (step.resolve === 'first-parent-child') {
        if (!pathMatchesStep(step, locationRef.current.pathname)) {
          await ensurePath(step.resolveFrom || '/parent');
          let href = null;
          for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
            if (runId !== runIdRef.current) return { action: 'abort' };
            href = findFirstParentChildHref();
            if (href) break;
            await sleep(WAIT_MS);
          }
          if (!href) {
            setPreparing(false);
            return step.optional ? { action: 'skip' } : { action: 'show' };
          }
          navigate(href);
          for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
            if (runId !== runIdRef.current) return { action: 'abort' };
            await sleep(WAIT_MS);
            if (pathMatchesStep(step, locationRef.current.pathname)) break;
          }
        }
      } else if (step.resolve === 'parent-child-tab-activity' || step.resolve === 'parent-child-tab-payment') {
        const tab = step.resolve === 'parent-child-tab-activity' ? 'activity' : 'payment';
        const m = locationRef.current.pathname.match(/^\/parent\/children\/([^/]+)/);
        if (m) {
          const next = `/parent/children/${m[1]}?tab=${tab}`;
          navigate(next);
          await sleep(WAIT_MS * 2);
        } else if (step.optional) {
          setPreparing(false);
          return { action: 'skip' };
        }
      } else if (step.path) {
        await ensurePath(step.path);
      }

      if (runId !== runIdRef.current) return { action: 'abort' };

      for (let i = 0; i < WAIT_ATTEMPTS; i += 1) {
        if (runId !== runIdRef.current) return { action: 'abort' };
        const el = findStepTarget(step.selector);
        if (el) {
          setPreparing(false);
          return { action: 'show' };
        }
        await sleep(WAIT_MS);
      }

      setPreparing(false);
      if (step.optional) return { action: 'skip' };
      return { action: 'show' };
    },
    [navigate],
  );

  const goToStep = useCallback(
    async (stepList, startIndex) => {
      const runId = runIdRef.current;
      let index = startIndex;
      try {
        while (index >= 0 && index < stepList.length) {
          const result = await prepareStep(stepList, index, runId);
          if (runId !== runIdRef.current) return;
          if (!result || result.action === 'abort') return;
          if (result.action === 'finish') {
            finish();
            return;
          }
          if (result.action === 'skip') {
            index += 1;
            continue;
          }
          setStepIndex(index);
          setActive(true);
          return;
        }
        finish();
      } catch (err) {
        console.error(err);
        if (runId !== runIdRef.current) return;
        finish();
      }
    },
    [finish, prepareStep],
  );

  const startChapter = useCallback(
    (nextChapterId, { force = false } = {}) => {
      if (!role) return;
      const chapter = getChapter(role, nextChapterId);
      if (!chapter) return;
      if (!force && readChapterDone(role, nextChapterId)) return;
      if (!hasCookieConsent()) return;
      if (chapter.homeOnly && !isTourHomePath(role, locationRef.current.pathname)) return;

      runIdRef.current += 1;
      setChapterId(nextChapterId);
      setSteps(chapter.steps);
      setStepIndex(0);
      setTargetRect(null);
      void goToStep(chapter.steps, 0);
    },
    [role, goToStep],
  );

  // Ждём закрытия cookie-баннера.
  useEffect(() => {
    if (cookieOk) return undefined;
    const id = window.setInterval(() => {
      if (hasCookieConsent()) {
        setCookieOk(true);
        window.clearInterval(id);
      }
    }, 300);
    return () => window.clearInterval(id);
  }, [cookieOk]);

  // Автозапуск карты + replay после навигации на главную.
  useEffect(() => {
    if (!role || !cookieOk) return undefined;

    const replay = consumeTourReplay();
    if (replay && replay.role === role) {
      const chapter = getChapter(role, replay.chapterId);
      if (chapter?.homeOnly && !isTourHomePath(role, location.pathname)) {
        // Ждём перехода на главную (меню профиля уже navigate'ит).
        return undefined;
      }
      const t = window.setTimeout(() => startChapter(replay.chapterId, { force: true }), 80);
      return () => window.clearTimeout(t);
    }

    if (!isTourHomePath(role, location.pathname)) return undefined;
    const auto = getAutoStartChapter(role);
    if (auto && !readChapterDone(role, auto.id)) {
      const t = window.setTimeout(() => startChapter(auto.id, { force: false }), 400);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [role, cookieOk, location.pathname, startChapter]);

  // Replay с главной / событие с карточек глав.
  useEffect(() => {
    function onReplay(e) {
      const r = e.detail?.role;
      const cid = e.detail?.chapterId || 'map';
      if (!r || r !== role) return;
      const chapter = getChapter(role, cid);
      if (chapter?.homeOnly && !isTourHomePath(role, location.pathname)) return;
      consumeTourReplay();
      startChapter(cid, { force: true });
    }
    function onStart(e) {
      const r = e.detail?.role;
      const cid = e.detail?.chapterId;
      if (!r || r !== role || !cid) return;
      startChapter(cid, { force: true });
    }
    window.addEventListener('diobuch-tour-replay', onReplay);
    window.addEventListener('diobuch-tour-start', onStart);
    return () => {
      window.removeEventListener('diobuch-tour-replay', onReplay);
      window.removeEventListener('diobuch-tour-start', onStart);
    };
  }, [role, location.pathname, startChapter]);

  const step = steps[stepIndex] || null;

  const measure = useCallback(() => {
    if (!step) {
      setTargetRect(null);
      return;
    }
    const el = findStepTarget(step.selector);
    if (!el) {
      setTargetRect(null);
      return;
    }
    el.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    const r = el.getBoundingClientRect();
    setTargetRect({
      top: r.top - PAD,
      left: r.left - PAD,
      width: r.width + PAD * 2,
      height: r.height + PAD * 2,
      bottom: r.bottom + PAD,
      right: r.right + PAD,
    });
  }, [step]);

  useLayoutEffect(() => {
    if (!active || !step || preparing) return undefined;
    const raf = window.requestAnimationFrame(() => measure());
    const onScroll = () => measure();
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [active, step, measure, viewport.w, viewport.h, preparing, location.pathname]);

  useEffect(() => {
    if (!active && !preparing) return undefined;
    function onKey(e) {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, preparing, finish]);

  const onNext = useCallback(() => {
    void goToStep(steps, stepIndex + 1);
  }, [goToStep, steps, stepIndex]);

  const onBack = useCallback(() => {
    void goToStep(steps, Math.max(0, stepIndex - 1));
  }, [goToStep, steps, stepIndex]);

  if ((!active && !preparing) || !step) return null;

  const isLast = stepIndex >= steps.length - 1;
  const hole = targetRect
    ? {
        x: Math.max(0, targetRect.left),
        y: Math.max(0, targetRect.top),
        w: Math.max(0, targetRect.width),
        h: Math.max(0, targetRect.height),
      }
    : null;

  const cardStyle = computeCardStyle(targetRect, viewport);
  const chapter = role && chapterId ? getChapter(role, chapterId) : null;

  return createPortal(
    <div className="site-tour" role="dialog" aria-modal="true" aria-labelledby="site-tour-title">
      <svg
        className="site-tour-mask"
        width={viewport.w}
        height={viewport.h}
        viewBox={`0 0 ${viewport.w} ${viewport.h}`}
        aria-hidden
      >
        <path
          className="site-tour-mask-shade"
          fill="rgba(2, 6, 23, 0.72)"
          fillRule="evenodd"
          d={tourShadePath(viewport.w, viewport.h, hole)}
        />
      </svg>
      {hole ? (
        <div
          className="site-tour-spotlight"
          style={{
            top: hole.y,
            left: hole.x,
            width: hole.w,
            height: hole.h,
          }}
          aria-hidden
        />
      ) : null}
      <div className="site-tour-card" style={cardStyle}>
        {preparing ? null : (
          <p className="site-tour-progress" aria-hidden>
            {chapter ? `${chapter.title} · ` : ''}
            {stepIndex + 1} / {steps.length}
          </p>
        )}
        <h2 id="site-tour-title" className="site-tour-title">
          {preparing ? 'Загрузка…' : step.title}
        </h2>
        <p className="site-tour-body">
          {preparing ? 'Подсказку можно пропустить и пользоваться сайтом.' : step.body}
        </p>
        <div className="site-tour-actions">
          <button type="button" className="btn btn-outline btn-sm" onClick={finish}>
            Пропустить
          </button>
          {preparing ? null : (
            <div className="site-tour-actions-nav">
              <button
                type="button"
                className="btn btn-outline btn-sm"
                disabled={stepIndex === 0}
                onClick={onBack}
              >
                Назад
              </button>
              {isLast ? (
                <button type="button" className="btn btn-primary btn-sm" onClick={finish}>
                  Понятно
                </button>
              ) : (
                <button type="button" className="btn btn-primary btn-sm" onClick={onNext}>
                  Далее
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
}
